import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionTimer from '../session/SessionTimer';
import ChatWidget from '../chat/ChatWidget';
import { useAuth } from '../../contexts/AuthContext';
import { useFilters, FilterProvider } from '../../contexts/FilterContext';
import FilterModal from '../modals/FilterModal';
import './MainLayout.css';

const MainLayoutContent = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const { isLoading: authLoading, user } = useAuth();
  const { isFilterModalOpen, handleProductFilterChange } = useFilters();
  const location = useLocation();

  const onProductSelectionChange = (selectedIds) => {
    console.log("Selected Product IDs for Filtering:", selectedIds);
    handleProductFilterChange(selectedIds);
  };
  // Handle sidebar toggle
  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle sidebar close
  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Show loading state during route transitions
  useEffect(() => {
    setIsRouteLoading(true);
    const timer = setTimeout(() => {
      setIsRouteLoading(false);
    }, 100); // Short delay to show loading state

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Ensure dark mode is never applied
  useEffect(() => {
    localStorage.removeItem('darkMode');
    document.documentElement.removeAttribute('data-theme');
  }, []);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        handleCloseSidebar();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSidebarOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // Skip to main content functionality
  const handleSkipToContent = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isFilterModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isFilterModalOpen]);

  if (authLoading) {
    return (
      <div className="auth-loading-container">
        <LoadingSpinner size="large" message="Authenticating..." />
      </div>
    );
  }

  return (
    <div className={`main-layout ${isSidebarOpen ? 'sidebar-open' : ''} ${isFilterModalOpen ? 'modal-open' : ''}`}>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="skip-to-content"
        onClick={handleSkipToContent}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSkipToContent();
          }
        }}
      >
        Skip to main content
      </a>

      {/* Header */}
      <Header userRole={user?.role || 'SUPER_ADMIN'}
        onToggleSidebar={handleToggleSidebar}
        isSidebarOpen={isSidebarOpen} />

      <div className="layout-body">
        {/* Sidebar */}
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
          onProductSelectionChange={onProductSelectionChange}
        />

        {/* Main Content Area */}
        <main
          id="main-content"
          className="main-content"
          role="main"
          aria-live="polite"
          aria-label="Main content"
          tabIndex={-1}
        >
          {/* Breadcrumb navigation */}
          {!['/inventory-overview', '/production-plans', '/dashboard', '/scenario-planning', '/production', '/financial-analysis'].includes(location.pathname) && (
            <div className="breadcrumb-container">
              <nav aria-label="Breadcrumb" className="breadcrumb">
                <ol className="breadcrumb-list">
                  <li className="breadcrumb-item">
                    <a href="/dashboard" className="breadcrumb-link">
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                      Home
                    </a>
                  </li>
                  {location.pathname !== '/dashboard' && (
                    <li className="breadcrumb-item">
                      <span className="breadcrumb-separator" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="breadcrumb-current" aria-current="page">
                        {getBreadcrumbLabel(location.pathname)}
                      </span>
                    </li>
                  )}
                </ol>
              </nav>

              {/* Session Timer - Aligned Right */}
              <div className="breadcrumb-session-timer">
                <SessionTimer />
              </div>
            </div>
          )}

          <div className="content-wrapper">
            {isRouteLoading ? (
              <div className="route-loading">
                <LoadingSpinner size="medium" message="Loading page..." />
              </div>
            ) : (
              <div className="page-content">
                <Outlet />
              </div>
            )}
          </div>

          {/* Footer */}
          <Footer />

          {/* Filter Modal */}
          <FilterModal />
        </main>
      </div>


      {/* Floating AI assistant */}
      <ChatWidget />

      {/* Focus trap for sidebar */}
      {isSidebarOpen && (
        <div
          className="focus-trap"
          tabIndex={0}
          onFocus={() => {
            const firstFocusableElement = document.querySelector(
              '.app-sidebar button, .app-sidebar a, .app-sidebar input, .app-sidebar select'
            );
            if (firstFocusableElement) {
              firstFocusableElement.focus();
            }
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// Helper function to get breadcrumb label from pathname
const getBreadcrumbLabel = (pathname) => {
  const pathMap = {
    '/dashboard': 'Dashboard',
    'inventory-overview': 'Inventory Dashboard',
    '/financial-analysis': 'Financial Analysis',
    '/scenario-planning': 'Scenario Planning',
    '/admin/users': 'User Management',
    '/profile': 'Profile',
    '/settings': 'Settings',
    '/support': 'Support',
    '/documentation': 'Documentation'
  };

  return pathMap[pathname] || pathname.split('/').pop()?.replace('-', ' ') || 'Page';
};

const MainLayout = () => {
  return (
    <FilterProvider>
      <MainLayoutContent />
    </FilterProvider>
  );
};

export default MainLayout;